import { stringToList } from '../../../config/resources/utils'
import { parseCaseStudy } from './caseStudy.parser'

/**
 * Converts a project name into a URL-safe slug.
 */
export const slugify = (value = '') =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')

const parseStringList = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }

  if (typeof value === 'string' && value.trim()) {
    return value
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return []
}

const buildSummary = (raw) => {
  if (raw.summary?.trim()) {
    return raw.summary.trim()
  }

  const description = raw.description?.trim() || ''

  if (description.length <= 180) {
    return description
  }

  return `${description.slice(0, 177).trim()}...`
}

const resolveTechnologies = (raw) => {
  if (Array.isArray(raw.technologiesArray)) {
    return raw.technologiesArray.filter(Boolean)
  }

  return stringToList((raw.technologies || '').toUpperCase(), '|').filter(Boolean)
}

const parseLinksValue = (raw) => {
  const value = raw.links

  if (value == null) {
    return null
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }

  return Array.isArray(value) ? value : null
}

const normalizeProjectLink = (link) => {
  if (!link || typeof link !== 'object') {
    return null
  }

  const url = link.url?.trim()
  const label = link.label?.trim()

  if (!url || !label) {
    return null
  }

  return { url, label }
}

const resolveLinks = (raw) => {
  const parsed = parseLinksValue(raw)

  if (!parsed) {
    return []
  }

  return parsed.map(normalizeProjectLink).filter(Boolean)
}

/**
 * Merges project row fields with optional case_study.hero overrides.
 */
export const resolveDetailHero = (project = {}) => {
  const hero = project.caseStudy?.hero

  return {
    title: hero?.title || project.name || '',
    subtitle: hero?.subtitle || project.summary || '',
    cover: hero?.cover || project.imagePath || '',
  }
}

/**
 * Normalizes API or static project records into a reusable portfolio shape.
 */
export const PROJECT_KINDS = {
  PERSONAL: 'personal',
  PRACTICE: 'practice',
}

const resolveKind = (raw) => {
  const kind = raw.kind?.trim?.() || raw.kind

  if (kind === PROJECT_KINDS.PERSONAL || kind === PROJECT_KINDS.PRACTICE) {
    return kind
  }

  return PROJECT_KINDS.PRACTICE
}

export const normalizeProject = (raw = {}) => {
  const slug = raw.slug || slugify(raw.name)

  return {
    id: raw.id,
    slug,
    name: raw.name || '',
    summary: buildSummary(raw),
    description: raw.description || '',
    image: raw.image || '',
    imagePath: raw.image ? `/images/portafolio/${raw.image}` : '',
    technologies: resolveTechnologies(raw),
    role: raw.role || 'Software Engineer',
    highlights: parseStringList(raw.highlights),
    year: raw.year || null,
    links: resolveLinks(raw),
    caseStudy: parseCaseStudy(raw),
    kind: resolveKind(raw),
    sortOrder: raw.sort_order ?? 0,
    isActive: raw.is_active ?? true,
  }
}

export const normalizeProjects = (projects = []) =>
  projects.map(normalizeProject).sort((a, b) => a.sortOrder - b.sortOrder)

export const filterProjectsByKind = (projects = [], kind = PROJECT_KINDS.PERSONAL) =>
  projects.filter((project) => project.kind === kind)

export const findProjectBySlug = (projects = [], slug = '') =>
  projects.find((project) => project.slug === slug)

export { parseCaseStudy, EMPTY_CASE_STUDY } from './caseStudy.parser'
